/**
 * Como se le cuenta un plan a una persona.
 *
 * Los limites viven en `plans.ts`; aqui vive la forma de decirlos. Estan
 * separados a proposito: `plans.ts` es lo que el servidor hace cumplir y no
 * debe depender de ningun idioma, y esto es escaparate.
 *
 * Y estan en UN solo sitio porque la portada y el panel describen los mismos
 * planes. Cuando cada uno tenia su propia lista, la portada enumeraba ocho
 * cosas y el panel tres: quien llegaba desde "Elegir Pro" se encontraba con
 * menos producto del que acababa de leer, justo en la pantalla donde saca la
 * tarjeta. Un plan se describe igual en todas partes o no se describe.
 */

import {
  MESES_DEL_ANO,
  MESES_QUE_SE_PAGAN,
  formatUsd,
  isUnlimited,
  yearlyTotalUsdCents,
  type Plan,
  type PlanLimits,
} from './plans.js';

/**
 * Cuantos codigos caben, dicho en cristiano.
 *
 * "1 códigos" no es espanol, y "Un código" a secas no dice de que.
 */
export function codigosDe(cuantos: number): string {
  if (isUnlimited(cuantos)) return 'Códigos QR sin límite';
  if (cuantos === 1) return 'Un código QR';
  return `${cuantos.toLocaleString('es-CO')} códigos QR`;
}

export function escaneosDe(almes: number): string {
  if (isUnlimited(almes)) return 'Escaneos sin límite';
  return `${almes.toLocaleString('es-CO')} escaneos al mes`;
}

/**
 * El historico, contado por lo que se ve y no por el numero de dias.
 *
 * "365 días de histórico" obliga a dividir entre 365 y a adivinar historico de
 * que. Se dice lo que es: hasta cuando puedes mirar atras, y de que.
 */
export function estadisticasDe(dias: number): string {
  if (isUnlimited(dias)) return 'Estadísticas de siempre';
  if (dias >= 730) return 'Estadísticas de los últimos dos años';
  if (dias >= 365) return 'Estadísticas del último año';
  if (dias >= 30) return `Estadísticas de los últimos ${Math.round(dias / 30)} meses`;
  return `Estadísticas de los últimos ${dias} días`;
}

/**
 * Las plazas del equipo.
 *
 * "15 personas" no decia de que, y "Solo tú" a secas tampoco: flotando en una
 * lista de ventajas se lee como "¿solo yo que?". Hace falta el verbo, porque
 * lo que se compra no son personas sino cuantas entran al panel a tocar los
 * codigos.
 */
export function equipoDe(personas: number): string {
  if (isUnlimited(personas)) return 'Entra a gestionarlos quien tú quieras';
  if (personas === 1) return 'Solo tú entras a gestionarlos';
  return `Entran ${personas} personas a gestionarlos`;
}

/**
 * Cuantos negocios separados caben.
 *
 * Es la linea que mas se malinterpreta si se dice en corto: "3 negocios" no
 * aclara que cada uno va por su cuenta, con sus codigos y su gente.
 */
export function negociosDe(cuantos: number): string {
  if (isUnlimited(cuantos)) return 'Los negocios que quieras, por separado';
  if (cuantos === 1) return 'Un negocio';
  return `Hasta ${cuantos} negocios, cada uno por su lado`;
}

export interface Rasgo {
  readonly texto: string;
  readonly incluido: boolean;
}

/**
 * Lo que trae un plan, en el orden en que se compara.
 *
 * Primero lo que se mide —codigos, escaneos, estadisticas, gente, negocios— y
 * despues lo que se tiene o no se tiene. Los ultimos son los que marcan la
 * diferencia entre el gratuito y los de pago, asi que van juntos al final.
 *
 * Cada linea dice QUE TE LLEVAS, no como se llama por dentro. "Destinos por
 * condición" es el nombre que le damos nosotros a la funcion; quien esta
 * decidiendo si paga no tiene por que saberlo.
 */
export function rasgosDe(l: PlanLimits): readonly Rasgo[] {
  return [
    { texto: codigosDe(l.codes), incluido: true },
    { texto: escaneosDe(l.scansPerMonth), incluido: true },
    { texto: estadisticasDe(l.analyticsRetentionDays), incluido: true },
    { texto: equipoDe(l.teamMembers), incluido: true },
    { texto: negociosDe(l.workspaces), incluido: true },
    { texto: 'Tu logo y tus colores en el código', incluido: l.customDesign },
    {
      texto: 'Lleva a un sitio u otro según el país o la hora',
      incluido: l.dynamicRouting,
    },
    // Nada de "cientos": el plan Pro tiene 50 y la propia tarjeta lo dice dos
    // lineas mas arriba. Lo que se vende no es la cantidad —esa ya esta en el
    // primer rasgo— sino no tener que rellenar el formulario uno por uno.
    { texto: 'Crear los códigos de golpe, no uno a uno', incluido: l.bulkCreate },
    /*
     * Falta aqui: la exportacion a CSV.
     *
     * `plans.ts` la marca como de pago, pero ningun sitio lo comprueba: el
     * boton "Exportar a CSV" de la analitica lo ve y lo pulsa tambien quien
     * esta en el gratuito. Anunciarla como ventaja de pago seria cobrar por
     * algo que ya esta regalado, y tacharla en la columna gratuita seria
     * negarle a esa persona una cosa que tiene delante.
     *
     * Se pone en la lista el dia que `limits.csvExport` decida algo.
     */
  ];
}

/**
 * La linea pequena de debajo del precio.
 *
 * En anual dice el total que se cobra de una vez, que es el dato que de verdad
 * le falta a alguien mirando un precio "por mes" que no se paga por meses.
 * Callarselo es la manera educada de dar una sorpresa en el cobro.
 */
export function comoSeCobra(plan: Plan, anual: boolean): string {
  if (!plan.monthlyPriceUsdCents) return 'Sin tarjeta y sin caducidad';
  if (!anual) return 'Se cobra cada mes';
  const total = yearlyTotalUsdCents(plan);
  return total === null ? 'Se cobra cada año' : `${formatUsd(total)} al año, de una vez`;
}

/**
 * Lo que se ahorra pagando el ano entero, dicho desde el ciclo mensual.
 *
 * Quien esta en "mes a mes" no ve el precio anual por ningun lado, asi que la
 * unica forma de enterarse del descuento es pulsar la otra pestana a ciegas.
 * Esta frase se lo pone delante sin obligarle a cambiar de vista.
 */
export function ahorroAnual(plan: Plan): string | null {
  const total = yearlyTotalUsdCents(plan);
  if (total === null || !plan.monthlyPriceUsdCents) return null;
  const meses = MESES_DEL_ANO - MESES_QUE_SE_PAGAN;
  return `${formatUsd(total)} al año: pagas ${MESES_QUE_SE_PAGAN} meses y te llevas ${meses}`;
}
